import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { Loader2 } from "lucide-react";
import { api } from "convex/_generated/api";
import { ClauseList } from "@/components/standards/ClauseList";
import { useTranslation } from "@/packages/utils";
import type { Id } from "@/packages/types";

// ─── Props ──────────────────────────────────────────────────────────────────

interface StepClausesProps {
  standardId: Id<"officialStandards">;
  auditId: Id<"audits">;
}

// ─── StepClauses ────────────────────────────────────────────────────────────

export function StepClauses({ standardId }: StepClausesProps) {
  const { t } = useTranslation("audits");
  const { data, isLoading } = useQuery(
    convexQuery(api.admin_standards_queries.getUserStandard, { standardId }),
  );

  if (isLoading || !data) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
        <span className="text-[12px] text-muted">
          {t("wizard.clauses.loading")}
        </span>
      </div>
    );
  }

  const { clauses } = data;

  return (
    <div className="flex flex-col gap-4 py-4">
      <div>
        <h3 className="text-sm font-medium text-primary">
          {t("wizard.clauses.extracted")}
        </h3>
        <p className="text-[12px] text-muted">
          {clauses.length} clause{clauses.length !== 1 ? "s" : ""} extracted
          from your document
        </p>
      </div>

      <div className="overflow-y-auto max-h-[55vh]">
        <ClauseList clauses={clauses} />
      </div>
    </div>
  );
}
